import React, { useEffect, useState, useMemo } from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import {Invitation, UserProfile } from '../types';
import {X, Shield, ShieldAlert, Loader2, MoreVertical, Plus, Search, Mail, UserMinus, Hash, Copy, Check, Inbox} from 'lucide-react';
import AlertModal from './AlertModal';

const TeamMembersModal: React.FC = () => {
    const {
        isMembersModalOpen,
        closeMembersModal,
        viewingTeamMembersId,
        fetchTeamMembers,
        userProfile,
        updateMemberRole,
        removeTeamMember,
        allUsers,
        sendInvitation,
        inviteUserByEmail,
        declineInvitation,
        showToast,
        listenToTeam,
        listenToPendingInvitations,
        listenToPendingEmailInvitations,
        approveJoinRequest,
        declineJoinRequest,
        cancelEmailInvitation,
        fetchUsersByIds
    } = useStandUp();

    const [members, setMembers] = useState<UserProfile[]>([]);
    const [teamInvitations, setTeamInvitations] = useState<Invitation[]>([]);
    const [emailInvitations, setEmailInvitations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [invitingId, setInvitingId] = useState<string | null>(null);
    const [emailInviteValue, setEmailInviteValue] = useState('');
    const [isEmailInviting, setIsEmailInviting] = useState(false);
    const [suggestedSearchQuery, setSuggestedSearchQuery] = useState('');
    const [teamName, setTeamName] = useState('');
    const [teamOwnerId, setTeamOwnerId] = useState<string | null>(null);
    const [pendingJoinRequests, setPendingJoinRequests] = useState<UserProfile[]>([]);
    const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
    const [memberToRemove, setMemberToRemove] = useState<{id: string, name: string} | null>(null);
    const [inviteToCancel, setInviteToCancel] = useState<{id: string, name: string, isEmail: boolean} | null>(null);
    const [locallyInvitedIds, setLocallyInvitedIds] = useState<Set<string>>(new Set()); // NEW: Optimistic state

    // Handle Escape Key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                closeMembersModal();
            }
        };
        if (isMembersModalOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isMembersModalOpen, closeMembersModal]);

    useEffect(() => {
        if (isMembersModalOpen && viewingTeamMembersId) {
            setLoading(true);
            setMembers([]);
            setLocallyInvitedIds(new Set()); // Clear on open
            setActiveMenuId(null);

            // 1. Fetch Team Doc
            const unsubTeam = listenToTeam(viewingTeamMembersId, (data) => {
                if (data) {
                    setTeamOwnerId(data.ownerId);
                    setTeamName(data.name); 

                    // Resolve pending requests
                    const pendingUids = data.pendingRequests || [];
                    if (pendingUids.length > 0) {
                        fetchUsersByIds(pendingUids).then(setPendingJoinRequests);
                    } else {
                        setPendingJoinRequests([]);
                    }
                }
            });

            // 2. Fetch Members
            fetchTeamMembers(viewingTeamMembersId)
                .then(fetchedMembers => {
                    setMembers(fetchedMembers);
                })
                .finally(() => setLoading(false));

            // 3. Listen for Pending Invitations for this team
            const unsubInvs = listenToPendingInvitations(viewingTeamMembersId, setTeamInvitations);

            // 4. Listen for Pending EMAIL Invitations for this team
            const unsubEmailInvs = listenToPendingEmailInvitations(viewingTeamMembersId, setEmailInvitations);

            return () => {
                unsubTeam();
                unsubInvs();
                unsubEmailInvs();
            };
        } else {
            setMembers([]);
            setTeamInvitations([]);
            setEmailInvitations([]);
            setTeamName('');
            setTeamOwnerId(null);
            setActiveMenuId(null);
            setSuggestedSearchQuery('');
        }
    }, [isMembersModalOpen, viewingTeamMembersId]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClick = () => setActiveMenuId(null);
        if (activeMenuId) window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, [activeMenuId]);

    // Filter Suggested Members
    const suggestedMembers = useMemo(() => {
        if (!viewingTeamMembersId) return [];
        const currentMemberIds = new Set(members.map(m => m.id));
        const pendingRequestIds = new Set(pendingJoinRequests.map(u => u.id));
        const pendingInviteeIds = new Set(teamInvitations.map(inv => inv.inviteeId));
        const pendingEmails = new Set(emailInvitations.map(inv => inv.email.toLowerCase()));
        
        let filtered = allUsers.filter(u => 
            !currentMemberIds.has(u.id) && 
            !pendingRequestIds.has(u.id) &&
            !pendingInviteeIds.has(u.id) && 
            !pendingEmails.has(u.email.toLowerCase()) &&
            !locallyInvitedIds.has(u.id) && // NEW
            u.id !== userProfile?.id
        );
        if (suggestedSearchQuery) {
            const q = suggestedSearchQuery.toLowerCase();
            filtered = filtered.filter(u => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
        }
        return filtered.sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [allUsers, members, pendingJoinRequests, teamInvitations, emailInvitations, locallyInvitedIds, userProfile?.id, suggestedSearchQuery]);

    const handleInvite = async (invitee: UserProfile) => {
        if (!viewingTeamMembersId) return;
        setInvitingId(invitee.id);
        try {
            await sendInvitation(viewingTeamMembersId, teamName, invitee.id);
            
            // OPTIMISTIC UI: Remove from list immediately
            setLocallyInvitedIds(prev => new Set([...prev, invitee.id]));
            
            showToast(`Invitation sent to ${invitee.displayName}`);
        } catch (e: any) { alert(e.message); }
        finally { setInvitingId(null); }
    };

    const handleApproveRequest = async (applicant: UserProfile) => {
        if (!viewingTeamMembersId) return;
        try {
            await approveJoinRequest(viewingTeamMembersId, teamName, applicant.id);
            showToast(`Approved ${applicant.displayName}`);
        } catch (e) { showToast("Failed to approve", "error"); }
    };

    const handleDeclineRequest = async (uid: string) => {
        if (!viewingTeamMembersId) return;
        try {
            await declineJoinRequest(viewingTeamMembersId, uid);
            showToast("Request declined");
        } catch (e) { console.error(e); }
    };

    const handleEmailInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!emailInviteValue.trim() || !viewingTeamMembersId) return;
        setIsEmailInviting(true);
        try {
            await inviteUserByEmail(emailInviteValue.trim().toLowerCase(), viewingTeamMembersId, teamName);
            setEmailInviteValue('');
            showToast(`Invite sent to ${emailInviteValue}`);
        } catch (e: any) {
            showToast(e.message || "Failed to send email invite.", "error");
        } finally { setIsEmailInviting(false); }
    };

    const sortedMembers = useMemo(() => {
        return [...members].sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [members]);

    if (!isMembersModalOpen || !viewingTeamMembersId) return null;

    const myRoleInThisTeam = userProfile?.teams?.find(t => t.teamId === viewingTeamMembersId)?.role || 'engineer';
    const amIManager = myRoleInThisTeam === 'manager';

    const handleRoleUpdate = async (memberId: string, newRole: 'manager' | 'engineer') => {
        setMembers(prev => prev.map(m => {
            if (m.id === memberId) {
                const newTeams = m.teams?.map(t => t.teamId === viewingTeamMembersId ? { ...t, role: newRole } : t) || [];
                return { ...m, teams: newTeams };
            }
            return m;
        }));
        try { await updateMemberRole(memberId, viewingTeamMembersId, newRole); }
        catch (e) { fetchTeamMembers(viewingTeamMembersId).then(setMembers); }
    };

    const handleRemoveMember = (memberId: string, memberName: string) => {
        setMemberToRemove({ id: memberId, name: memberName });
        setActiveMenuId(null);
    };

    const confirmRemoveMember = async () => {
        if (!memberToRemove || !viewingTeamMembersId) return;
        const { id: memberId, name: memberName } = memberToRemove;
        setMembers(prev => prev.filter(m => m.id !== memberId));
        setMemberToRemove(null);
        try {
            await removeTeamMember(memberId, viewingTeamMembersId);
            showToast(`Removed ${memberName} from team.`);
        } catch (e) { fetchTeamMembers(viewingTeamMembersId).then(setMembers); }
    };

    const confirmCancelInvite = async () => {
        if (!inviteToCancel) return;
        const { id, name, isEmail } = inviteToCancel;
        
        try {
            if (isEmail) {
                await cancelEmailInvitation(id);
            } else {
                await declineInvitation(id);
            }
            showToast(`Cancelled invitation for ${name}`);
        } catch (e) {
            console.error(e);
            showToast("Failed to cancel invitation", "error");
        } finally {
            setInviteToCancel(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-page-content overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Team Members</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{teamName}</p>
                    </div>
                    <button onClick={closeMembersModal} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-6">
                    {/* Access Requests */}
                    {amIManager && pendingJoinRequests.length > 0 && (
                        <div className="px-3 pt-2">
                            <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <Inbox size={12} /> Pending Access Requests
                            </div>
                            <div className="space-y-2">
                                {pendingJoinRequests.map(u => (
                                    <div key={u.id} className="flex items-center justify-between p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 shadow-sm">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <img src={u.photoURL || `https://ui-avatars.com/api/?name=${u.displayName}`} className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 shadow-sm" alt="" />
                                            <div className="min-w-0">
                                                <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{u.displayName}</p>
                                                <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 ml-2">
                                            <button onClick={() => handleApproveRequest(u)} className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all shadow-md shadow-emerald-500/20 active:scale-95" title="Approve"><Check size={14} /></button>
                                            <button onClick={() => handleDeclineRequest(u.id)} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-red-500 hover:border-red-500 rounded-lg transition-all" title="Decline"><X size={14} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Invite Code Section */}
                    {amIManager && (
                        <div className="px-3 pt-2">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Invite via Team Code</div>
                            <div className="flex gap-2">
                                <code className="flex-1 bg-gray-50 dark:bg-gray-900 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-300 flex items-center justify-between">
                                    <span className="flex items-center gap-1.5"><Hash size={12} className="text-gray-400" /> {viewingTeamMembersId}</span>
                                </code>
                                <button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(viewingTeamMembersId || '');
                                        showToast("Invite code copied!");
                                    }}
                                    className="px-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-[10px] font-bold rounded-lg border border-gray-200 dark:border-gray-700 transition-all flex items-center gap-1.5"
                                >
                                    <Copy size={12} /> Copy
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Email Invite Section */}
                    {amIManager && (
                        <div className="px-3 pt-2">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Invite New Collaborator</div>
                            <form onSubmit={handleEmailInvite} className="flex gap-2">
                                <div className="relative flex-1">
                                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="email"
                                        placeholder="colleague@company.com"
                                        value={emailInviteValue}
                                        onChange={(e) => setEmailInviteValue(e.target.value)}
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-10 pr-3 py-2 text-sm focus:ring-2 ring-indigo-500 outline-none dark:text-white transition-all"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isEmailInviting || !emailInviteValue.includes('@')}
                                    className="px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-all active:scale-95 flex items-center justify-center min-w-[80px]"
                                >
                                    {isEmailInviting ? <Loader2 size={14} className="animate-spin" /> : 'Send Invite'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Suggested Members Section */}
                    {amIManager && (allUsers.length > (members.length + 1)) && (
                        <div className="px-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Suggested Collaborators</div>
                                {suggestedMembers.length > 0 && (
                                    <span className="text-[9px] text-gray-400 bg-gray-100 dark:bg-gray-700/50 px-1.5 py-0.5 rounded font-medium">{suggestedMembers.length} available</span>
                                )}
                            </div>
                            <div className="relative mb-3">
                                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input 
                                    type="text"
                                    placeholder="Quick search organization..."
                                    value={suggestedSearchQuery}
                                    onChange={(e) => setSuggestedSearchQuery(e.target.value)}
                                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-8 py-1.5 text-[11px] focus:ring-2 ring-brand-500 outline-none dark:text-white transition-all"
                                />
                                {suggestedSearchQuery && (
                                    <button 
                                        onClick={() => setSuggestedSearchQuery('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                            
                            {suggestedMembers.length > 0 ? (
                                <div className="space-y-1 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                                    {suggestedMembers.slice(0, 5).map(u => (
                                        <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700 hover:border-brand-200 transition-colors">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <img src={u.photoURL || `https://ui-avatars.com/api/?name=${u.displayName}`} className="w-6 h-6 rounded-full" alt="" />
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{u.displayName}</p>
                                                    <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleInvite(u)}
                                                disabled={invitingId === u.id}
                                                className="ml-2 p-1.5 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors shrink-0 disabled:opacity-50"
                                            >
                                                {invitingId === u.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                            </button>
                                        </div>
                                    ))}
                                    {suggestedMembers.length > 5 && (
                                        <p className="text-[9px] text-center text-gray-400 py-1 italic">Type to search more members...</p>
                                    )}
                                </div>
                            ) : (
                                <div className="py-4 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                                    <p className="text-[10px] text-gray-400 italic">No matching collaborators found.</p>
                                    <button onClick={() => setSuggestedSearchQuery('')} className="text-[10px] text-brand-500 font-bold mt-1 hover:underline">Clear Search</button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Members List */}
                    <div>
                        <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Current Members</div>
                        {loading ? (
                            <div className="flex justify-center items-center h-32 text-brand-500">
                                <Loader2 className="animate-spin" size={32} />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1 pb-4">
                                {sortedMembers.map(member => {
                                    const membership = member.teams?.find(t => t.teamId === viewingTeamMembersId);
                                    const role = membership?.role || 'engineer';
                                    const isMngr = role === 'manager';
                                    const isOwner = member.id === teamOwnerId;
                                    const isMe = member.id === userProfile?.id;
                                    const canEdit = amIManager && !isOwner && !isMe;

                                    return (
                                        <div key={member.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group relative">
                                            <img src={member.photoURL || `https://ui-avatars.com/api/?name=${member.displayName}`} alt={member.displayName} className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 object-cover shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-sm text-gray-900 dark:text-white truncate flex items-center gap-2">
                                                    {member.displayName}
                                                    {isMe && <span className="text-gray-400 font-normal text-[10px]">(You)</span>}
                                                    {isOwner && <span className="text-[9px] bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-1.5 py-0.5 rounded font-bold uppercase tracking-tighter text-nowrap">Owner</span>}
                                                </div>
                                                <div className="text-[11px] text-gray-500 truncate">{member.email}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className={`px-2 py-1 rounded text-[9px] font-bold uppercase flex items-center gap-1 ${isMngr ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                                    {isMngr ? <ShieldAlert size={10}/> : <Shield size={10}/>} {role}
                                                </div>
                                                {canEdit && (
                                                    <div className="relative">
                                                        <button onClick={(e) => { e.stopPropagation(); setActiveMenuId(activeMenuId === member.id ? null : member.id); }} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"><MoreVertical size={16} /></button>
                                                        {activeMenuId === member.id && (
                                                            <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden py-1">
                                                                <button onClick={() => handleRoleUpdate(member.id, isMngr ? 'engineer' : 'manager')} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                                                                    {isMngr ? <Shield size={14} /> : <ShieldAlert size={14} />} {isMngr ? 'Demote to Engineer' : 'Promote to Manager'}
                                                                </button>
                                                                <button onClick={() => handleRemoveMember(member.id, member.displayName)} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"><UserMinus size={14} /> Remove from Team</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Pending Invitees */}
                                {teamInvitations.map(inv => {
                                    const invitee = allUsers.find(u => u.id === inv.inviteeId);
                                    if (!invitee) return null;

                                    return (
                                        <div key={inv.id} className="flex items-center gap-3 p-3 rounded-lg opacity-60 grayscale bg-gray-50/50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-700">
                                            <img src={invitee.photoURL || `https://ui-avatars.com/api/?name=${invitee.displayName}`} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-sm text-gray-900 dark:text-white truncate flex items-center gap-2">
                                                    {invitee.displayName}
                                                    <span className="text-[9px] bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 px-1.5 py-0.5 rounded font-bold uppercase tracking-tighter">Pending</span>
                                                </div>
                                                <div className="text-[11px] text-gray-500 truncate">{invitee.email}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="text-[9px] font-bold text-gray-400 uppercase italic">Waiting...</div>
                                                {amIManager && (
                                                    <button 
                                                        onClick={() => setInviteToCancel({ id: inv.id, name: invitee.displayName, isEmail: false })}
                                                        className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 transition-all"
                                                        title="Cancel Invitation"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Pending EMAIL Invitees */}
                                {emailInvitations.map(inv => (
                                    <div key={inv.id} className="flex items-center gap-3 p-3 rounded-lg opacity-60 grayscale bg-gray-50/50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-700">
                                        <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400 shrink-0">
                                            <Mail size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-col">
                                                <div className="font-semibold text-sm text-gray-900 dark:text-white truncate pr-1">
                                                    {inv.email}
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-[8px] bg-indigo-50 text-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-400 px-1 py-0.5 rounded font-black uppercase tracking-tighter shrink-0">
                                                        Email Sent
                                                    </span>
                                                    <span className="text-[8px] font-bold text-gray-400 uppercase italic shrink-0">
                                                        Waiting...
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-[10px] text-gray-400 mt-1">Invited by {inv.inviterName || 'Someone'}</div>
                                        </div>
                                        <div className="flex items-center shrink-0">
                                            {amIManager && (
                                                <button 
                                                    onClick={() => setInviteToCancel({ id: inv.id, name: inv.email, isEmail: true })}
                                                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"
                                                    title="Cancel Invitation"
                                                >
                                                    <X size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AlertModal 
                isOpen={!!memberToRemove}
                title="Remove Member"
                message={`Are you sure you want to remove ${memberToRemove?.name} from this team?`}
                confirmText="Remove"
                isDestructive={true}
                onConfirm={confirmRemoveMember}
                onCancel={() => setMemberToRemove(null)}
            />

            <AlertModal 
                isOpen={!!inviteToCancel}
                title="Cancel Invitation"
                message={`Are you sure you want to cancel the invitation for ${inviteToCancel?.name}? They will no longer be able to join using that invite.`}
                confirmText="Cancel Invitation"
                isDestructive={true}
                onConfirm={confirmCancelInvite}
                onCancel={() => setInviteToCancel(null)}
            />
        </div>
    );
};

export default TeamMembersModal;
